import * as React from "react";
import { Box, Stack, Typography, Button } from "@mui/material";
import type { CupidCard } from "../types/dashboard";
import CupidCardItem from "../components/CupidCardItem";
import { shouldRenderCard, getCardDisplayStatus } from "../../../utils/cardStatus";
import { parseKSTDate } from "../../../utils/dateUtils";

type Props = {
  cards: CupidCard[];
  walletHearts: number;
  onOpen: (cardId: string) => void;
  onExtend: (cardId: string) => void;
  onChat: (card: CupidCard) => void;
};

function ActiveChatCard({ card, onChat }: { card: CupidCard; onChat: (card: CupidCard) => void }) {
  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const deadlineMs = card.chatDeadline ? parseKSTDate(card.chatDeadline).getTime() - now.getTime() : 0;
  const daysLeft = deadlineMs / (24 * 60 * 60 * 1000);
  const chipColor = daysLeft <= 1 ? '#E53E3E' : daysLeft <= 3 ? '#FF8C00' : '#4A5A72';
  const hours = Math.floor(deadlineMs / (60 * 60 * 1000));
  const countdownLabel = daysLeft >= 1
    ? `D-${Math.ceil(daysLeft)}`
    : hours > 0 ? `${hours}시간 남음` : '곧 종료';

  const imageSrc = card.thumbnailImage.photoUrl;

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: '14px',
      bgcolor: '#FFFDF5', border: '1.5px solid #F5A623',
      borderRadius: '14px', p: '14px 16px',
    }}>
      {/* 썸네일 */}
      {imageSrc ? (
        <Box component="img" src={imageSrc} alt="프로필"
          sx={{ width: 52, height: 52, borderRadius: '10px', objectFit: 'cover', flexShrink: 0 }} />
      ) : (
        <Box sx={{ width: 52, height: 52, borderRadius: '10px', bgcolor: '#E8E0D8', flexShrink: 0 }} />
      )}

      {/* 이름 + 카운트다운 */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1A1A1A', mb: '3px' }}>
          {card.cardPreview.name ? `${card.cardPreview.name} 님` : `○○○ 님`}
          {card.cardPreview.age ? ` · ${card.cardPreview.age}세` : ''}
        </Typography>
        <Box sx={{
          display: 'inline-block',
          bgcolor: `${chipColor}18`, color: chipColor,
          fontSize: 12, fontWeight: 700,
          px: '9px', py: '3px', borderRadius: '7px',
        }}>
          💬 대화 마감 {countdownLabel}
        </Box>
      </Box>

      {/* 대화 버튼 */}
      <Button
        variant="contained"
        size="small"
        onClick={() => onChat(card)}
        sx={{
          height: 40, borderRadius: '10px', px: '14px', flexShrink: 0,
          bgcolor: '#0D1F3C', color: 'white', fontWeight: 700, fontSize: 14,
          '&:hover': { bgcolor: '#0A1830' },
        }}
      >
        대화 하기 →
      </Button>
    </Box>
  );
}

export default function CupidCardsSection({
  cards,
  walletHearts,
  onOpen,
  onExtend,
  onChat,
}: Props) {
  // 콜백 메모이제이션으로 불필요한 리렌더링 방지
  const memoizedOnOpen = React.useCallback(onOpen, [onOpen]);
  const memoizedOnExtend = React.useCallback(onExtend, [onExtend]);
  const memoizedOnChat = React.useCallback(onChat, [onChat]);

  const now = new Date();

  // "진행 중인 대화" — DEAD 상태지만 chatDeadline이 아직 남은 상호 카드
  const activeChatCards = cards.filter(card =>
    card.isMutual &&
    card.chatDeadline &&
    parseKSTDate(card.chatDeadline) > now &&
    getCardDisplayStatus(card, now) === 'DEAD'
  );

  // 일반 표시 카드 — 상호 카드 먼저, 이후 cardId 오름차순
  const displayCards = cards
    .filter(card => shouldRenderCard(card, now))
    .sort((a, b) => {
      if (a.isMutual !== b.isMutual) return a.isMutual ? -1 : 1;
      return Number(a.cardId) - Number(b.cardId);
    });

  // 다음 월요일 11:00 KST 계산 (카드 발급 시점)
  const getNextDeliveryLabel = (): string => {
    const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
    const day = kst.getUTCDay(); // 0=일, 1=월, ...
    const hour = kst.getUTCHours();
    let daysUntilMonday = (1 - day + 7) % 7;
    if (daysUntilMonday === 0 && hour >= 11) daysUntilMonday = 7;
    if (daysUntilMonday === 0) return "오늘 오전 11시에 새 카드가 도착합니다";
    if (daysUntilMonday === 1) return "내일 오전 11시에 새 카드가 도착합니다";
    return `${daysUntilMonday}일 후 월요일 오전 11시에 새 카드가 도착합니다`;
  };

  const hasCards = displayCards.length > 0 || activeChatCards.length > 0;

  return (
    <Stack spacing={2.5}>
      {/* 진행 중인 대화 섹션 — DEAD 상호 카드 중 chatDeadline이 남은 것 */}
      {activeChatCards.length > 0 && (
        <Box>
          <Typography variant="h6" sx={{
            fontFamily: '"Playfair Display", serif',
            fontSize: { xs: '18px', md: '20px' }, fontWeight: 700, mb: 1,
          }}>
            💬 진행 중인 대화
          </Typography>
          <Stack spacing={1.5}>
            {activeChatCards.map(card => (
              <ActiveChatCard key={card.cardId} card={card} onChat={memoizedOnChat} />
            ))}
          </Stack>
        </Box>
      )}

      <Box>
        <Typography variant="h6" sx={{ fontFamily: '"Playfair Display", serif', fontSize: { xs: '20px', md: '22px' }, fontWeight: 700 }}>
          이번 주 큐피트 카드
        </Typography>
        <Typography sx={{ color: "text.secondary", fontSize: 13, mt: 0.5 }}>
          {hasCards ? "새로운 카드부터 확인하세요" : getNextDeliveryLabel()}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(2, 1fr)",
          },
          gap: { xs: 2, sm: 3 },
          maxWidth: "100%",
          alignItems: "start",
        }}
      >
        {displayCards.map((card) => (
          <CupidCardItem
            key={card.cardId}
            card={card}
            walletHearts={walletHearts}
            onOpen={memoizedOnOpen}
            onExtend={memoizedOnExtend}
            onChat={memoizedOnChat}
          />
        ))}
      </Box>
    </Stack>
  );
}